import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import RichTextEditor from '../components/RichTextEditor';
import SubjectSelector from '../components/SubjectSelector';
import { LectureAiChip, SurfaceHero } from '../components/design-kit';
import { BookText, Eye, FilePlus2, Loader2, Save, Search, Share2 } from 'lucide-react';

function htmlToPlainText(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
}

export default function NotesPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [title, setTitle] = useState('');
  const [subjectId, setSubjectId] = useState(null);
  const [html, setHtml] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const saveTimerRef = useRef(null);
  const initializedRef = useRef(false);

  const filteredNotes = useMemo(() => {
    const query = search.trim().toLowerCase();
    const sorted = [...notes].sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
    if (!query) return sorted;
    return sorted.filter((note) => {
      const haystack = `${note.title || ''} ${note.formatted_notes || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [notes, search]);

  const loadNotes = async () => {
    try {
      const data = await api.getLectures();
      setNotes(data || []);
      return data || [];
    } catch (error) {
      toast.error(error.message || 'Failed to load notes');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!filteredNotes.length) {
      setSelectedNote(null);
      initializedRef.current = false;
      return;
    }

    const explicit = id ? filteredNotes.find((note) => note.id === id) : null;
    const next = explicit || filteredNotes[0];
    if (!selectedNote || selectedNote.id !== next.id) {
      setSelectedNote(next);
      setTitle(next.title || 'Untitled note');
      setSubjectId(next.subject_id || null);
      setHtml(next.formatted_notes_html || '<p></p>');
      setLastSavedAt(next.updated_at || next.created_at || null);
      initializedRef.current = false;
      if (!id || id !== next.id) {
        navigate(`/notes/${next.id}`, { replace: true });
      }
    }
  }, [filteredNotes, id, loading, navigate, selectedNote]);

  useEffect(() => {
    if (!selectedNote) return;
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      setSaving(true);
      try {
        const updated = await api.updateLecture(selectedNote.id, {
          title: title.trim() || 'Untitled note',
          subject_id: subjectId || '',
          formatted_notes_html: html,
          formatted_notes: htmlToPlainText(html),
          status: 'completed'
        });
        setSelectedNote(updated);
        setNotes((prev) => prev.map((note) => (note.id === updated.id ? updated : note)));
        setLastSavedAt(updated.updated_at || updated.created_at || null);
      } catch (error) {
        toast.error(error.message || 'Failed to save note');
      } finally {
        setSaving(false);
      }
    }, 800);

    return () => window.clearTimeout(saveTimerRef.current);
  }, [html, subjectId, title, selectedNote]);

  const handleCreateNote = async () => {
    setCreating(true);
    try {
      const created = await api.createLecture({
        title: 'Untitled note',
        raw_transcript: '',
        subject_id: null,
      });
      const seeded = await api.updateLecture(created.id, {
        title: 'Untitled note',
        formatted_notes: '',
        formatted_notes_html: '<p></p>',
        status: 'completed',
      });
      setNotes((prev) => [seeded, ...prev]);
      setSelectedNote(seeded);
      setTitle(seeded.title || 'Untitled note');
      setSubjectId(seeded.subject_id || null);
      setHtml(seeded.formatted_notes_html || '<p></p>');
      setLastSavedAt(seeded.updated_at || seeded.created_at || null);
      initializedRef.current = false;
      navigate(`/notes/${seeded.id}`);
      toast.success('New note ready');
    } catch (error) {
      toast.error(error.message || 'Failed to create note');
    } finally {
      setCreating(false);
    }
  };

  const buildSharePreviewUrl = (noteId) => `${window.location.origin}/shared/note/${noteId}`;

  const handleCopyShareLink = async () => {
    if (!selectedNote) return;
    try {
      await navigator.clipboard.writeText(buildSharePreviewUrl(selectedNote.id));
      toast.success('Shared note link copied');
    } catch (error) {
      toast.error('Failed to copy share link');
    }
  };

  return (
    <div className="flex-1 overflow-hidden bg-background">
      <div className="mx-auto flex h-full max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <SurfaceHero
          eyebrow="Notes"
          title="Polished study notes that keep up with you"
          detail="Use this as your working document surface. Notes autosave, stay attached to subjects, and can be indexed into memory for later retrieval."
          aside={(
            <>
              <Badge variant="outline" className="h-9 rounded-xl px-3 text-xs">
                {saving ? 'Saving...' : lastSavedAt ? `Saved ${formatDistanceToNow(new Date(lastSavedAt), { addSuffix: true })}` : 'Not saved yet'}
              </Badge>
              <Button onClick={handleCreateNote} disabled={creating} className="rounded-xl">
                {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus2 className="mr-2 h-4 w-4" />}
                New note
              </Button>
            </>
          )}
        />

        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <Card className="flex min-h-0 flex-col overflow-hidden border-border/60 bg-card/70 shadow-card">
            <div className="border-b border-border/60 p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search notes"
                  className="h-10 rounded-xl pl-9"
                />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading notes...
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="px-3 py-6 text-sm text-muted-foreground">No notes yet. Create one to start your shared study workspace.</div>
              ) : (
                filteredNotes.map((note) => {
                  const active = selectedNote?.id === note.id;
                  return (
                    <button
                      key={note.id}
                      type="button"
                      onClick={() => navigate(`/notes/${note.id}`)}
                      className={`mb-2 flex w-full flex-col rounded-2xl border px-3 py-3 text-left transition-colors ${
                        active ? 'border-primary/30 bg-primary/5' : 'border-transparent hover:border-border hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <BookText className="h-4 w-4 text-muted-foreground" />
                        <p className="truncate font-medium text-foreground">{note.title || 'Untitled note'}</p>
                      </div>
                      <div className="mt-2">
                        <LectureAiChip label="Note" tone="note" />
                      </div>
                      <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{note.formatted_notes || 'Empty note'}</p>
                      <p className="mt-3 text-[11px] text-muted-foreground">
                        {formatDistanceToNow(new Date(note.updated_at || note.created_at), { addSuffix: true })}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </Card>

          <Card className="flex min-h-0 flex-col overflow-hidden border-border/60 bg-card/70 shadow-card">
            {selectedNote ? (
              <>
                <div className="border-b border-border/60 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <Input
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder="Untitled note"
                      className="h-11 rounded-xl border-0 bg-background/80 px-0 text-xl font-semibold shadow-none focus-visible:ring-0"
                    />
                    <div className="flex items-center gap-2">
                      <SubjectSelector currentSubjectId={subjectId} onSelect={setSubjectId} showTriggerDot={false} />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-xl"
                        onClick={() => window.open(buildSharePreviewUrl(selectedNote.id), '_blank', 'noopener,noreferrer')}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Preview
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-xl"
                        onClick={handleCopyShareLink}
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        Copy share link
                      </Button>
                      <Badge variant="outline" className="rounded-xl px-3 py-2 text-xs">
                        <Save className="mr-1 h-3.5 w-3.5" />
                        Autosave
                      </Badge>
                      <LectureAiChip label="Share-ready note styling" tone="note" />
                    </div>
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                  <RichTextEditor
                    content={html}
                    onChange={setHtml}
                    suggestions={selectedNote.ai_suggestions || []}
                    className="rounded-2xl border border-border/60 bg-background/85"
                    placeholder="Start writing. Refine lecture notes, build study guides, and let the app remember the important parts."
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
                Select a note to start editing.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
